import { api } from './baseAPI';
import { ApiResponse, ListResponse } from '../types/api';

// 수업 일정 변경 요청 관련 타입 정의
export interface LessonScheduleModificationData {
  originalScheduleId: number;
  changeType: 'permanent' | 'temporary';
  changePeriod?: 'oneWeek' | 'oneMonth' | 'custom';
  changeStartDate: string;
  changeEndDate?: string;
  reason: string;
  requestedDate?: string;
  requestedStartTime?: string;
  requestedEndTime?: string;
}

// 레거시 수정 요청 데이터 (하위 호환성)
export interface LegacyModificationRequestData {
  requestType: string;
  changeType: string;
  changePeriod?: string;
  changeStartDate?: string;
  changeEndDate?: string;
  requestMessage?: string;
  requestedDate?: string;
  requestedStartTime?: string | null;
  requestedEndTime?: string | null;
  requestedDayOfWeek?: string;
  lessonScheduleId?: number | string | null;
}

export interface LessonScheduleModification {
  id: number;
  modificationId: string;
  originalScheduleId: number;
  userId: number;
  changeType: 'permanent' | 'temporary';
  changePeriod?: 'oneWeek' | 'oneMonth' | 'custom';
  changeStartDate: string;
  changeEndDate?: string;
  requestedDate?: string;
  requestedStartTime?: string;
  requestedEndTime?: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  teacherResponse?: string;
  handledByTeacher?: number;
  approvedAt?: string;
  rejectedAt?: string;
  cancelledAt?: string;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

// 수업 일정 변경 요청 API
export const modificationAPI = {
  // 수업 일정 변경 요청 생성
  createScheduleModification: async (requestData: LessonScheduleModificationData): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.post('/lesson-schedule/modifications', requestData);
    return response.data;
  },

  // 수업 일정 변경 요청 목록 조회
  getScheduleModifications: async (params?: {
    userId?: number;
    status?: string;
    changeType?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<LessonScheduleModification[]>> => {
    const response = await api.get('/lesson-schedule/modifications', { params });
    return response.data;
  },

  // 수업 일정 변경 요청 상세 조회
  getScheduleModification: async (modificationId: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.get(`/lesson-schedule/modifications/${modificationId}`);
    return response.data;
  },

  // 수업 일정 변경 요청 승인
  approveScheduleModification: async (modificationId: string, response?: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const responseData = response ? { teacherResponse: response } : {};
    const apiResponse = await api.patch(`/lesson-schedule/modifications/${modificationId}/approve`, responseData);
    return apiResponse.data;
  },

  // 수업 일정 변경 요청 거절
  rejectScheduleModification: async (modificationId: string, reason: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.patch(`/lesson-schedule/modifications/${modificationId}/reject`, { reason });
    return response.data;
  },

  // 수업 일정 변경 요청 취소
  cancelScheduleModification: async (modificationId: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.patch(`/lesson-schedule/modifications/${modificationId}/cancel`);
    return response.data;
  },

  // 기존 수정 요청 API (하위 호환성을 위해 유지)
  createModificationRequest: async (requestData: LegacyModificationRequestData): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.post('/modification', requestData);
    return response.data;
  },

  getModificationRequests: async (params?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse<ListResponse<LessonScheduleModification>>> => {
    const response = await api.get('/modification', { params });
    return response.data;
  },

  getModificationRequest: async (requestId: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.get(`/modification/${requestId}`);
    return response.data;
  },

  approveModificationRequest: async (requestId: string, response?: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const responseData = response ? { teacherResponse: response } : {};
    const apiResponse = await api.put(`/modification/${requestId}/approve`, responseData);
    return apiResponse.data;
  },

  rejectModificationRequest: async (requestId: string, reason: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.put(`/modification/${requestId}/reject`, { teacherResponse: reason });
    return response.data;
  },

  cancelModificationRequest: async (requestId: string): Promise<ApiResponse<LessonScheduleModification>> => {
    const response = await api.put(`/modification/${requestId}/cancel`);
    return response.data;
  },

  // 사용자: 자신의 pending 요청 확인 (중복 요청 방지용)
  checkPendingRequests: async (): Promise<ApiResponse<LessonScheduleModification[]>> => {
    const response = await api.get('/modification/check-pending');
    return response.data;
  },

  // 사용자: 자신의 모든 요청 내역 조회
  getMyModificationRequests: async (params?: { status?: string; page?: number; limit?: number }): Promise<ApiResponse<ListResponse<LessonScheduleModification>>> => {
    const response = await api.get('/modification/my-requests', { params });
    return response.data;
  }
}; 